import React, {useState, useEffect} from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import {useDispatch, useSelector} from 'react-redux';
import {useFormInput} from '../../utils/utilities';
import Select from '../Select';
import CustomButton from '../CustomButton';
import {
  addCustomer,
  editCustomer,
  getBuildings,
  getEntrancesByBuilding,
} from '../../actions';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';
import { getAllBuildings } from '../../actions/buildingsActions';

const CustomerModal = ({customer}) => {
  const dispatch = useDispatch();
  const firstName = useFormInput(customer?.firstName || '');
  const lastName = useFormInput(customer?.lastName || '');
  const email = useFormInput(customer?.email || '');
  const phoneNumber = useFormInput(customer?.phoneNumber || '');
  const birthday = useFormInput(
    !!customer
      ? moment(customer.birthday).format('yyyy-MM-DD')
      : moment('1990-01-01').format('yyyy-MM-DD'),
  );
  const flatNumber = useFormInput(customer?.flatNumber || '');
  const contract = useFormInput(customer?.contract || '');
  const identificationCard = useFormInput(customer?.identificationCard || '');
  const {buildings} = useSelector(s => s.buildings);
  const {allBuildings , allBuildingsLoading} = useSelector(s => s.buildings);
  const {buildingEntrances, entrancesLoading} = useSelector(s => s.entrances);
  const {customersLoading, customersError} = useSelector(s => s.customers);
  const [building, setBuilding] = useState(customer?.buildingId || '');
  const [entrance, setEntrance] = useState(customer?.entranceId || '');

  const onChangeBuilding = value => {
    setBuilding(value);
    setEntrance('');
    dispatch(getEntrancesByBuilding(value));
  };
  const onChangeEntrance = value => {
    setEntrance(value);
  };

  const onSubmit = e => {
    e.preventDefault();
    const birthdayMs = moment(birthday.value).add(1, 'day').valueOf();
    const values = [
      email.value,
      firstName.value,
      lastName.value,
      birthdayMs,
      +flatNumber.value,
      entrance,
      phoneNumber.value,
      identificationCard.value,
      +contract.value,
      building,
    ];
    if (customer) {
      dispatch(editCustomer(...values, customer.id));
    } else {
      dispatch(addCustomer(...values));
    }
  };

  useEffect(() => {
    dispatch(getAllBuildings());
    if (customer) {
      dispatch(getEntrancesByBuilding(customer.buildingId));
    }
  }, []);

  useEffect(() => {
    if (customer) {
      dispatch(getEntrancesByBuilding(customer.buildingId));
      firstName.manuallyChange(customer.firstName);
      lastName.manuallyChange(customer.lastName);
      email.manuallyChange(customer.email);
      birthday.manuallyChange(moment(customer.birthday).format('yyyy-MM-DD'));
      flatNumber.manuallyChange(customer.flatNumber);
      setEntrance(customer.entranceId);
      phoneNumber.manuallyChange(customer.phoneNumber);
      contract.manuallyChange(customer.contract);
      setBuilding(customer.buildingId);
    }
  }, [customer]);

  return (
    <>
      {!customer && (
        <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
          Add new Customer
        </Typography>
      )}
      <form onSubmit={onSubmit}>
        <Box
          display="flex"
          flexWrap="wrap"
          justifyContent="center"
          mt="2rem"
          gap="1rem">
          <TextField
            {...firstName}
            error={isFieldIvalid(customersError, 'firstName')}
            helperText={fieldError(customersError, 'firstName')}
            required
            label="First name"
            sx={{
              width: '45%',
            }}
          />
          <TextField
            {...lastName}
            error={isFieldIvalid(customersError, 'lastName')}
            helperText={fieldError(customersError, 'lastName')}
            required
            label="Last name"
            sx={{
              width: '45%',
            }}
          />
          <TextField
            {...email}
            required
            label="Email"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'email')}
            helperText={fieldError(customersError, 'email')}
          />
          <TextField
            type={'date'}
            {...birthday}
            required
            label="Birthday"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'birthday')}
            helperText={fieldError(customersError, 'birthday')}
          />
          <TextField
            {...phoneNumber}
            required
            label="Phone number"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'phoneNumber')}
            helperText={fieldError(customersError, 'phoneNumber')}
          />
          <Select
            items={allBuildings}
            value={building}
            onChangeValue={onChangeBuilding}
            label="Building"
            width="45%"
            required
            error={isFieldIvalid(customersError, 'buildingId')}
            helperText={fieldError(customersError, 'buildingId')}
          />
          <Select
            items={buildingEntrances}
            value={entrance}
            onChangeValue={onChangeEntrance}
            label="Entrance"
            disabled={!building || entrancesLoading}
            width="45%"
            required
            error={isFieldIvalid(customersError, 'entranceId')}
            helperText={fieldError(customersError, 'entranceId')}
          />
          <TextField
            {...flatNumber}
            required
            type="number"
            label="Flat number"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'flatNumber')}
            helperText={fieldError(customersError, 'flatNumber')}
          />
          <TextField
            {...identificationCard}
            required
            label="Identification card"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'identificationCard')}
            helperText={fieldError(customersError, 'identificationCard')}
          />
          <TextField
            {...contract}
            required
            type="number"
            label="Contract"
            sx={{
              width: '45%',
            }}
            error={isFieldIvalid(customersError, 'contract')}
            helperText={fieldError(customersError, 'contract')}
          />
          <CustomButton
            loading={customersLoading}
            type="submit"
            sx={{
              width: '60%',
            }}>
            Submit
          </CustomButton>
        </Box>
      </form>
    </>
  );
};

export default CustomerModal;
